/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect } from "react";

import {
  Badge,
  Box,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
} from "@material-ui/core";

import {
  Create,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";

import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import { columnNumberField, columnTextField } from "utils/tables-utils";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import { useWebsocketFetch } from "hooks/useWebsocketFetch";
import { useVisionLogin } from "hooks/useVisionLogin";
import { utils, writeFileXLSX } from "xlsx";
import { API_ENV } from "constants/environment";
import { IsMobile } from "utils/is-mobile";

const useCustomStyles = makeStyles((theme) => ({
  badge: ({ status }) => ({
    backgroundColor: status
      ? theme.palette.success.main
      : theme.palette.error.main,
    height: 20,
    width: 20,
    borderRadius: 10,
    border: "none",
  }),
  root: {
    margin: "0 0.5rem",
  },
}));

const exportIconStyle = {
  width: 25,
  height: 25,
};

const ColoredStatus = ({ status }) => {
  const classes = useCustomStyles({ status });

  return (
    <Tooltip
      title={status ? "Falha Resolvida" : "Falha Pendente"}
      placement="top"
    >
      <Badge
        overlap="circle"
        badgeContent=" "
        color="primary"
        classes={{ badge: classes.badge, root: classes.root }}
      />
    </Tooltip>
  );
};

const getTableColumns = (setSelectedRecord, toggleFormDialog, searching) => [
  {
    dataField: "SucessoUltimaOcorrencia",
    text: "Status",
    align: "center",
    isDummyField: true,
    csvExport: false,
    headerAlign: "center",
    formatter: (cell, row) => {
      const status = Boolean(row.UsuarioResolucao);

      return <ColoredStatus status={status} />;
    },
  },
  {
    dataField: "opcoes",
    text: "Opções",
    isDummyField: true,
    csvExport: false,
    headerStyle: { width: 45 },
    formatter: (cell, row) => {
      return searching ? null : (
        <Tooltip
          title={
            row.UsuarioResolucao
              ? "Não é possível editar falha com resolução"
              : "Editar Falha"
          }
        >
          <span>
            <IconButton
              aria-label="edit"
              onClick={() => {
                setSelectedRecord(row);
                toggleFormDialog("edit");
              }}
              disabled={Boolean(row.UsuarioResolucao)}
            >
              <Box
                component={Create}
                width="1.25rem!important"
                height="1.25rem!important"
              />
            </IconButton>
          </span>
        </Tooltip>
      );
    },
  },
  columnNumberField(
    {
      dataField: "UsuarioLancamento",
      text: "Usuário",
      sort: false,
      formatter: (cell) => (searching ? <Skeleton variant="text" /> : cell),
    },
    false
  ),
  columnTextField(
    {
      dataField: "DataHoraLancamentoFormat",
      text: "Data de Registro",
      formatter: (cell) => (searching ? <Skeleton variant="text" /> : cell),
      align: "center",
      sort: false,
    },
    false
  ),
  columnNumberField(
    {
      dataField: "IdTear",
      text: "Número Tear",
      sort: false,
      formatter: (cell) => (searching ? <Skeleton variant="text" /> : cell),
    },
    false
  ),
  columnNumberField(
    {
      dataField: "IdPista",
      text: "Pista",
      sort: false,
      formatter: (cell) => (searching ? <Skeleton variant="text" /> : cell),
    },
    false
  ),
  columnTextField(
    {
      dataField: "DescMotivoFalha",
      text: "Motivo da Falha",
      formatter: (cell, row) =>
        searching ? <Skeleton variant="text" /> : row.Motivo?.DescMotivoFalha,
    },
    false
  ),
  columnTextField(
    {
      dataField: "QuantidadeFalha",
      text: "Quantidade de Falhas",
      formatter: (cell) => (searching ? <Skeleton variant="text" /> : cell),
    },
    false
  ),
  columnTextField(
    {
      dataField: "Observacao",
      text: "Observação",
      formatter: (cell) => (searching ? <Skeleton variant="text" /> : cell),
    },
    false
  ),
  columnTextField(
    {
      dataField: "TearTurnoResolucao.Descricao",
      text: "Turno da Resolução",
      formatter: (cell) =>
        searching ? <Skeleton variant="text" /> : cell || "",
      align: "center",
    },
    false
  ),
  columnTextField(
    {
      dataField: "DataHoraResolucaoFormat",
      text: "Data de Resolução",
      formatter: (cell) => (searching ? <Skeleton variant="text" /> : cell),
      align: "center",
    },
    false
  ),
];

const useStyles = makeStyles(componentStyles);

export default function TableRegistrarFalha({
  dataTable,
  totalRegisters,
  currentPage,
  currentSizePerPage,
  handleSearch,
  setSelectedRecord,
  toggleFormDialog,
  searching,
  tableTitle,
  lastSearchParams,
}) {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { userId } = useVisionLogin();

  const [loading, setLoading] = useState(false);
  const [sendRequestExport, setSendRequestExport] = useState(null);

  const { data, handleFetchData, status, handleFetchUrl } = useWebsocketFetch({
    onSuccess: handleDownloadXML,
  });

  const isMobile = IsMobile();

  const doSearch = useCallback(async (page, sizePerPage) => {
    setLoading(true);

    await handleSearch({ page, sizePerPage });

    setLoading(false);
  }, []);

  const handlePageChange = useCallback(
    (page, sizePerPage) => {
      doSearch(page, sizePerPage);
    },
    [doSearch]
  );

  const handleSizePerPageChange = useCallback(
    (sizePerPage, page) => {
      doSearch(page, sizePerPage);
    },
    [doSearch]
  );

  const handleSendMessage = (message) => {
    handleFetchData(message);
    snackbar.showMessage("Aguarde exportação do relatório.", {
      severity: "info",
      autoHideDuration: 60000,
    });
  };

  function handleDownloadXML() {
    const receipt = utils.json_to_sheet(data);

    const wb = utils.book_new();
    utils.book_append_sheet(wb, receipt, "RegistroFalhas.xlsx");
    writeFileXLSX(wb, "RegistroFalhas.xlsx");
  }

  const exportCmp =
    status === "on" ? (
      <>
        <span>
          <Tooltip title="Exportar tudo">
            <IconButtonLoader
              onClick={() => handleSendMessage(sendRequestExport)}
              icon={
                <img
                  src="https://img.icons8.com/material-outlined/72/000000/xls-export.png"
                  alt="ícone clicável para exportar a tabela no formato Excel"
                  style={exportIconStyle}
                />
              }
              size="small"
            />
          </Tooltip>
        </span>
      </>
    ) : status === "fetching" ? (
      <span>
        <CircularProgress style={exportIconStyle} />
      </span>
    ) : null;

  useEffect(() => {
    if (status === "empty") {
      snackbar.showMessage("Sua busca não retornou nenhum resultado.", {
        severity: "success",
      });
    }
  }, [status]);

  useEffect(() => {
    if (lastSearchParams && userId) {
      setSendRequestExport({
        action: "weave-register-list",
        params: {
          querystring: lastSearchParams,
          header: {
            "K-IdUser": userId,
          },
        },
      });
    }
  }, [lastSearchParams, userId]);

  useEffect(() => {
    handleFetchUrl(
      `wss://0s3sx9lhna.execute-api.us-east-1.amazonaws.com/${API_ENV}`
    );
  }, []);

  return (
    <ToolkitTable
      searched={Boolean(dataTable)}
      keyField="IdLancamentoFalha"
      dataTable={dataTable}
      columnModel={getTableColumns(
        setSelectedRecord,
        toggleFormDialog,
        searching
      )}
      headerTitle={tableTitle}
      csvFileName="RegistroFalhas.csv"
      showSearch={false}
      showPrint
      loading={loading}
      pagination={{
        options: [25, 50, 100],
        defaultOption: currentSizePerPage,
        showTotal: true,
        totalSize: totalRegisters,
        page: currentPage,
        onPageChange: handlePageChange,
        sizePerPage: currentSizePerPage,
        onSizePerPageChange: handleSizePerPageChange,
      }}
      allowEmpty
      noResultTitle="Não há registros de falhas cadastrados."
      noResultSubtitle="Clique em + Registrar Falha para começar!"
      bootstrapTableProps={{
        remote: true,
        onTableChange: () => {},
      }}
      notFoundTitle="Sua pesquisa não encontrou nenhum registro de falha."
      CustomHeader={() => (
        <Grid
          container
          className="pl-4"
          style={{
            padding: isMobile ? "0" : "0 24px",
            display: "flex",
            alignItems: isMobile ? "flex-start" : "center",
            flexDirection: isMobile ? "column" : "row",
            marginBottom: !isMobile && "1rem",
          }}
        >
          <Grid
            item
            xs={isMobile ? 12 : 6}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Box component={Typography} variant="h3" marginBottom="0!important">
              <Box component="span">{tableTitle}</Box>
              <IconButtonLoader
                size="medium"
                extraStyle={{
                  width: 24,
                  height: 24,
                  marginLeft: "0.3rem",
                }}
                title="Recarregar lista"
                onClick={() =>
                  handleSearch({
                    page: currentPage,
                    sizePerPage: currentSizePerPage,
                  })
                }
                icon={<RefreshIcon color="primary" />}
                aria-label="refresh"
                color="primary"
                disabled={searching}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={isMobile ? 12 : 6}
            sm={isMobile ? 12 : 6}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              padding: 0,
              marginTop: isMobile && "1rem",
              marginBottom: isMobile ? "1rem" : "0.5rem!important",
            }}
          >
            <Box
              justifyContent={isMobile ? "flex-start" : "flex-end"}
              alignItems="center"
              display="flex"
              flexWrap="wrap"
              style={{
                marginLeft: isMobile ? 10 : 15,
                marginRight: isMobile ? 5 : 15,
                cursor: "pointer",
              }}
            >
              <Button
                variant="contained"
                classes={{
                  root: classes.buttonRootWhite,
                }}
                onClick={() => toggleFormDialog("create")}
                disabled={searching}
                style={{ marginRight: "1rem" }}
              >
                <Box component={AddIcon} position="relative" top="-2px" />{" "}
                Registrar Falha
              </Button>
              {exportCmp}
            </Box>
          </Grid>
        </Grid>
      )}
    />
  );
}
